import { Grid, Typography, useMediaQuery, useTheme } from "@mui/material";
import { observer } from "mobx-react";
import { useCallback, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useCurrentDashboard } from "../useCurrentDashboard";
import { useStore } from "utils/useStore";
import SignalsPageStore from "../Store/SignalsPageStore";
import { usePDF } from "react-to-pdf";
import { useViewportLazyLoad } from "./useViewportLazyLoad";
import DashboardHeader from "./DashboardHeader";
import DashboardLoading from "./DashboardLoading";
import SignalsDashboardGrid from "./Layout/SignalsDashboardGrid";
import { AuthStore } from "stores/AuthStore";
import { SignalsAudioStatus, SignalsObjectType } from "../SignalsModels";
import MontageCardStore from "../BaseObjects/MontageCard/MontageCardStore";
import { HideImageRounded } from "@mui/icons-material";
import DashboardEmpty from "./DashboardEmpty";
import StandardDashboardBanner from "./StandardDashboardBanner";
import { getDefaultDashboardPdfFileName } from "../SignalsUtils";
import DataAvailabilityFilterBanner from "./DataAvailabilityFilterBanner";

// The main body of a dashboard
const DashboardBody = observer(() => {
    const currentDashboard = useCurrentDashboard();
    const location = useLocation();

    const theme = useTheme();
    const isWide = useMediaQuery("(min-width: 1000px)");
    const isThin = useMediaQuery("(max-width: 800px)");

    const { toPDF, targetRef } = usePDF();

    const exportToPDF = useCallback(
        async (options?: Parameters<typeof toPDF>[0]) => {
            if (!currentDashboard) return;

            const chartsReady =
                await currentDashboard.loadAllComponentsForExport();
            if (!chartsReady) return;
            // Allow React effects (e.g. AI summary triggers) to fire now that
            // charts have finished rendering.
            await new Promise<void>((resolve) =>
                requestAnimationFrame(() => resolve()),
            );
            const summariesReady =
                await currentDashboard.waitForSummariesAfterExport();
            if (!summariesReady) return;
            // Final rAF: wait for the browser to paint before PDF capture.
            await new Promise<void>((resolve) =>
                requestAnimationFrame(() => resolve()),
            );
            if (!currentDashboard.beginExportCapture()) return;

            const defaultFilename = getDefaultDashboardPdfFileName(
                currentDashboard.title,
            );
            try {
                // Capture mode hides some card chrome, and cards loaded for the
                // export may still be fitting their content, so let card
                // heights and positions settle first.
                await currentDashboard.dashboardLayoutStore.waitForSettledLayout();
                await toPDF({
                    filename: defaultFilename,
                    ...options,
                    overrides: {
                        ...options?.overrides,
                        canvas: {
                            ...options?.overrides?.canvas,
                            // Adjusting the scroll top prevents the dashboard
                            // title and filters from getting cut off in the PDF
                            // when the dashboard is scrolled down.
                            onclone: (_document, clonedDashboard) => {
                                if (clonedDashboard.parentElement) {
                                    clonedDashboard.parentElement.scrollTop = 0;
                                }
                            },
                        },
                    },
                });
            } finally {
                currentDashboard.endExportCapture();
            }
        },
        [currentDashboard, toPDF],
    );

    const signalsPageStore = useStore(SignalsPageStore);
    const authStore = useStore(AuthStore);
    const dashboardsLoading = signalsPageStore.getTaskLoading("LoadDashboards");
    const { attachRef } = useViewportLazyLoad(currentDashboard ?? null);
    const currentDashboardId = currentDashboard?.id;
    const currentDashboardComponentCount =
        currentDashboard?.components.length ?? 0;

    useEffect(() => {
        const hashTargetId = decodeURIComponent(location.hash.slice(1));

        if (!hashTargetId || !currentDashboardId || dashboardsLoading) {
            return;
        }

        let isCancelled = false;
        let attempts = 0;
        const maxAttempts = 20;

        const scrollToHashTarget = () => {
            if (isCancelled) return;

            const hashTarget = document.getElementById(hashTargetId);

            if (hashTarget) {
                hashTarget.scrollIntoView({
                    behavior: "smooth",
                    block: "start",
                });
                return;
            }

            attempts += 1;
            if (attempts < maxAttempts) {
                window.setTimeout(scrollToHashTarget, 100);
            }
        };

        scrollToHashTarget();

        return () => {
            isCancelled = true;
        };
    }, [
        location.hash,
        currentDashboardId,
        currentDashboardComponentCount,
        dashboardsLoading,
    ]);

    if (dashboardsLoading) {
        return <DashboardLoading />;
    }

    if (!currentDashboard) {
        return (
            <Grid
                container
                direction="column"
                padding={1}
                wrap="nowrap"
                sx={{ backgroundColor: theme.palette.lightgray.background }}
            />
        );
    }

    const montageStores = currentDashboard.components.filter(
        (component) => component.objectType === SignalsObjectType.MontageCard,
    ) as MontageCardStore[];

    const restrictMediaPermissionOnAndMontageExists =
        authStore.canUserView("Restrict Media") &&
        !authStore.isUserUltra() &&
        montageStores.length > 0;

    const hiddenMontageExists = montageStores.some(
        (store) => store.data?.status === SignalsAudioStatus.DoesNotHaveAccess,
    );

    const showHiddenCardsBanner =
        restrictMediaPermissionOnAndMontageExists ||
        hiddenMontageExists ||
        currentDashboard.signalsChartCount !== currentDashboard.chartCount;

    return (
        <Grid
            container
            direction="column"
            paddingTop={0}
            paddingRight={1}
            paddingBottom={1}
            paddingLeft={1}
            wrap="nowrap"
            style={
                currentDashboard.isPresentationMode
                    ? {
                          backgroundColor: theme.palette.lightgray.background,
                          position: "absolute",
                          top: 0,
                          paddingLeft: isWide
                              ? theme.spacing(25)
                              : isThin
                              ? theme.spacing(5)
                              : theme.spacing(12.5),
                          paddingRight: isWide
                              ? theme.spacing(25)
                              : isThin
                              ? theme.spacing(5)
                              : theme.spacing(12.5),
                      }
                    : { backgroundColor: theme.palette.lightgray.background }
            }
            ref={targetRef} // We need to attach the ref to the part we want to export as PDF, which is why we drill the toPDF part so far down
        >
            <DashboardHeader toPDF={exportToPDF} />
            <DataAvailabilityFilterBanner
                authStore={authStore}
                filters={currentDashboard.effectiveAppliedFilters}
            />
            {showHiddenCardsBanner && (
                <Grid
                    item
                    sx={{
                        display: "flex",
                        flexDirection: "row",
                        padding: 2,
                        gap: 2,
                        backgroundColor: theme.palette.gray[200],
                        border: `0.5px solid ${theme.palette.gray[400]}`,
                        borderRadius: 1,
                        alignSelf: "stretch",
                        alignItems: "center",
                        marginLeft: 2,
                        marginRight: 2,
                        marginBottom: 1,
                    }}
                >
                    <HideImageRounded
                        sx={{
                            height: "20px",
                            width: "20px",
                            color: theme.palette.gray[500],
                        }}
                    />
                    <Typography
                        sx={{
                            fontWeight: 600,
                            color: theme.palette.neutral.main,
                        }}
                    >
                        Some cards are hidden due to your permissions.
                    </Typography>
                </Grid>
            )}
            <StandardDashboardBanner
                dashboardType={currentDashboard.dashboardType}
            />

            <Grid
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    px: 2,
                    paddingTop: 2,
                    paddingBottom: 3,
                }}
            >
                <SignalsDashboardGrid
                    dashboardStore={currentDashboard}
                    attachRef={attachRef}
                />
                {currentDashboard.components.length === 0 && <DashboardEmpty />}
            </Grid>
        </Grid>
    );
});

export default DashboardBody;
